/**
 * Body + head + tail + legs draw routines for the procedural horse.
 * Kept separate from the jockey drawing so each module stays small.
 */

import { drawJockey } from './horse-procedural-jockey.js';
import { FRONT_LEG_FRAMES, BACK_LEG_FRAMES, type LegSegments } from './horse-procedural-legs.js';
import * as P from './horse-procedural-palette.js';

export interface RenderArgs {
  frame: number;
  silkColor: string;
  number: number;
  len: number;
  thick: number;
}

export function renderHorseInto(c: CanvasRenderingContext2D, args: RenderArgs): void {
  drawTail(c, args);
  drawBackLegs(c, args);
  drawBody(c, args);
  drawHindquarters(c, args);
  drawChest(c, args);
  drawNeck(c, args);
  drawHead(c, args);
  drawMane(c, args);
  drawFrontLegs(c, args);
  drawJockey(c, args);
}

// ---------------------------------------------------------------------------
// Body + saddle
// ---------------------------------------------------------------------------

function drawBody(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const { len, thick } = a;
  const grad = c.createLinearGradient(0, -thick / 2, 0, thick / 2);
  grad.addColorStop(0, P.BODY_HIGHLIGHT);
  grad.addColorStop(0.55, P.BODY);
  grad.addColorStop(1, P.BODY_SHADE);
  c.fillStyle = grad;
  c.beginPath();
  c.moveTo(-len / 2 + 14, -thick / 2 + 2);
  c.quadraticCurveTo(-12, -thick / 2 - 4, 6, -thick / 2 + 2);
  c.quadraticCurveTo(len / 2 - 18, -thick / 2 - 2, len / 2 - 22, -thick / 2 + 8);
  c.quadraticCurveTo(len / 2 - 26, thick / 2 - 2, 0, thick / 2 + 2);
  c.quadraticCurveTo(-len / 2 + 12, thick / 2, -len / 2 + 14, -thick / 2 + 2);
  c.closePath();
  c.fill();
  c.fillStyle = 'rgba(0,0,0,0.22)';
  c.beginPath();
  c.ellipse(-4, thick / 2 - 4, len / 2 - 18, 6, 0, 0, Math.PI * 2);
  c.fill();
  drawSaddlePad(c, thick);
}

function drawSaddlePad(c: CanvasRenderingContext2D, thick: number): void {
  c.fillStyle = 'rgba(245,240,230,0.95)';
  c.beginPath();
  c.moveTo(-22, -thick / 2 + 1);
  c.quadraticCurveTo(-6, -thick / 2 - 8, 14, -thick / 2 + 2);
  c.lineTo(14, -thick / 2 + 6);
  c.quadraticCurveTo(-4, -thick / 2 + 2, -22, -thick / 2 + 6);
  c.closePath();
  c.fill();
  c.strokeStyle = 'rgba(80,40,20,0.6)';
  c.lineWidth = 1.5;
  c.beginPath();
  c.moveTo(-22, -thick / 2 + 6);
  c.lineTo(14, -thick / 2 + 6);
  c.stroke();
}

function drawHindquarters(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const { len, thick } = a;
  const grad = c.createRadialGradient(-len / 2 + 12, -8, 4, -len / 2 + 12, -8, 36);
  grad.addColorStop(0, P.BODY_HIGHLIGHT);
  grad.addColorStop(1, P.BODY);
  c.fillStyle = grad;
  c.beginPath();
  c.ellipse(-len / 2 + 14, 2, 22, thick / 2 + 4, 0.05, 0, Math.PI * 2);
  c.fill();
  c.strokeStyle = 'rgba(0,0,0,0.25)';
  c.lineWidth = 1.5;
  c.beginPath();
  c.moveTo(-len / 2 + 18, -thick / 2 + 6);
  c.quadraticCurveTo(-len / 2 + 6, 0, -len / 2 + 18, thick / 2 - 4);
  c.stroke();
}

function drawChest(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const { len, thick } = a;
  const grad = c.createRadialGradient(len / 2 - 22, 0, 4, len / 2 - 22, 0, 28);
  grad.addColorStop(0, P.BODY_HIGHLIGHT);
  grad.addColorStop(1, P.BODY);
  c.fillStyle = grad;
  c.beginPath();
  c.ellipse(len / 2 - 24, 4, 16, thick / 2 + 2, -0.1, 0, Math.PI * 2);
  c.fill();
  c.strokeStyle = 'rgba(0,0,0,0.22)';
  c.lineWidth = 1.5;
  c.beginPath();
  c.moveTo(len / 2 - 30, -thick / 2 + 8);
  c.quadraticCurveTo(len / 2 - 36, 4, len / 2 - 24, thick / 2 - 4);
  c.stroke();
}

// ---------------------------------------------------------------------------
// Neck + head + mane
// ---------------------------------------------------------------------------

function drawNeck(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const { len, thick } = a;
  const baseX = len / 2 - 24;
  const baseY = -thick / 2 + 6;
  const tipX = len / 2 + 8;
  const tipY = -thick / 2 - 22;
  const grad = c.createLinearGradient(baseX, baseY, tipX, tipY);
  grad.addColorStop(0, P.BODY);
  grad.addColorStop(1, P.BODY_HIGHLIGHT);
  c.fillStyle = grad;
  c.beginPath();
  c.moveTo(baseX - 4, baseY + 4);
  c.quadraticCurveTo(baseX + 12, baseY - 24, tipX + 2, tipY + 4);
  c.lineTo(tipX + 14, tipY + 10);
  c.quadraticCurveTo(baseX + 22, baseY - 4, baseX + 6, baseY + 10);
  c.closePath();
  c.fill();
  c.strokeStyle = 'rgba(0,0,0,0.3)';
  c.lineWidth = 1.5;
  c.beginPath();
  c.moveTo(baseX - 2, baseY);
  c.quadraticCurveTo(baseX + 14, baseY - 22, tipX, tipY + 4);
  c.stroke();
}

function drawHead(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const { len, thick } = a;
  c.save();
  c.translate(len / 2 + 8, -thick / 2 - 22);
  const grad = c.createLinearGradient(0, -10, 0, 18);
  grad.addColorStop(0, P.BODY_HIGHLIGHT);
  grad.addColorStop(1, P.BODY);
  c.fillStyle = grad;
  c.beginPath();
  c.moveTo(-4, 12);
  c.quadraticCurveTo(-8, -2, -2, -10);
  c.quadraticCurveTo(8, -14, 18, -10);
  c.quadraticCurveTo(28, -8, 30, -2);
  c.quadraticCurveTo(32, 6, 22, 10);
  c.quadraticCurveTo(14, 14, 4, 14);
  c.closePath();
  c.fill();
  c.fillStyle = 'rgba(0,0,0,0.22)';
  c.beginPath();
  c.ellipse(24, 6, 7, 4, 0, 0, Math.PI * 2);
  c.fill();
  drawBlaze(c);
  drawEyeAndNostril(c);
  drawEars(c);
  drawBridle(c);
  c.restore();
}

function drawBlaze(c: CanvasRenderingContext2D): void {
  c.fillStyle = 'rgba(245,240,230,0.85)';
  c.beginPath();
  c.moveTo(8, -10);
  c.quadraticCurveTo(14, -2, 18, 8);
  c.quadraticCurveTo(13, 9, 9, 4);
  c.quadraticCurveTo(6, -2, 8, -10);
  c.closePath();
  c.fill();
}

function drawEyeAndNostril(c: CanvasRenderingContext2D): void {
  c.fillStyle = '#fff';
  c.beginPath();
  c.arc(6, -4, 2.4, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = '#0d0703';
  c.beginPath();
  c.arc(6.6, -3.6, 1.4, 0, Math.PI * 2);
  c.fill();
  c.beginPath();
  c.ellipse(26, 4, 2, 1.4, 0.3, 0, Math.PI * 2);
  c.fill();
  c.strokeStyle = 'rgba(0,0,0,0.45)';
  c.lineWidth = 1;
  c.beginPath();
  c.moveTo(20, 11);
  c.lineTo(28, 9);
  c.stroke();
}

function drawEars(c: CanvasRenderingContext2D): void {
  c.fillStyle = P.BODY;
  c.beginPath();
  c.moveTo(-2, -10);
  c.lineTo(2, -22);
  c.lineTo(8, -12);
  c.closePath();
  c.fill();
  c.fillStyle = P.BODY_SHADE;
  c.beginPath();
  c.moveTo(2, -22);
  c.lineTo(4, -16);
  c.lineTo(7, -13);
  c.closePath();
  c.fill();
  c.fillStyle = P.BODY;
  c.beginPath();
  c.moveTo(6, -12);
  c.lineTo(11, -22);
  c.lineTo(15, -10);
  c.closePath();
  c.fill();
}

function drawBridle(c: CanvasRenderingContext2D): void {
  c.strokeStyle = 'rgba(40,20,10,0.85)';
  c.lineWidth = 2;
  c.beginPath();
  c.moveTo(2, -2);
  c.quadraticCurveTo(14, 0, 26, 4);
  c.stroke();
  c.beginPath();
  c.moveTo(10, -8);
  c.lineTo(14, 4);
  c.stroke();
}

function drawMane(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const { len, thick } = a;
  c.fillStyle = P.BODY_DARK;
  c.beginPath();
  c.moveTo(len / 2 + 6, -thick / 2 - 28);
  c.quadraticCurveTo(len / 2 - 4, -thick / 2 - 18, -8, -thick / 2 - 4);
  c.lineTo(-12, -thick / 2 + 4);
  c.quadraticCurveTo(len / 2 - 18, -thick / 2 - 8, len / 2 + 2, -thick / 2 - 22);
  c.closePath();
  c.fill();
  c.strokeStyle = 'rgba(255,255,255,0.18)';
  c.lineWidth = 1;
  c.beginPath();
  c.moveTo(len / 2 - 8, -thick / 2 - 18);
  c.lineTo(len / 2 - 26, -thick / 2 - 6);
  c.moveTo(len / 2 - 22, -thick / 2 - 14);
  c.lineTo(len / 2 - 38, -thick / 2 - 2);
  c.moveTo(0, -thick / 2 - 8);
  c.lineTo(-12, -thick / 2);
  c.stroke();
}

function drawTail(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const { len, frame } = a;
  const wave = (frame === 0 || frame === 3) ? -10 : 8;
  const lift = (frame === 2) ? -6 : 2;
  c.fillStyle = P.BODY_DARK;
  c.beginPath();
  c.moveTo(-len / 2 + 18, -8 + lift);
  c.quadraticCurveTo(-len / 2 - 8, -4 + lift + wave * 0.3, -len / 2 - 22, 18 + lift + wave);
  c.quadraticCurveTo(-len / 2 - 30, 28 + lift, -len / 2 - 18, 30 + lift);
  c.quadraticCurveTo(-len / 2 - 6, 16 + lift, -len / 2 + 6, 8 + lift);
  c.closePath();
  c.fill();
  c.strokeStyle = 'rgba(255,255,255,0.12)';
  c.lineWidth = 1;
  c.beginPath();
  c.moveTo(-len / 2 + 10, -2 + lift);
  c.quadraticCurveTo(-len / 2 - 4, 4 + lift + wave * 0.3, -len / 2 - 16, 22 + lift);
  c.stroke();
}

// ---------------------------------------------------------------------------
// Legs (data tables in horse-procedural-legs.ts)
// ---------------------------------------------------------------------------

function drawFrontLegs(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const cfg = FRONT_LEG_FRAMES[a.frame] ?? FRONT_LEG_FRAMES[0]!;
  drawArticulatedLeg(c, cfg.far, '#3a1f10', 4.5);
  drawArticulatedLeg(c, cfg.near, P.BODY_DARK, 5.5);
}

function drawBackLegs(c: CanvasRenderingContext2D, a: RenderArgs): void {
  const cfg = BACK_LEG_FRAMES[a.frame] ?? BACK_LEG_FRAMES[0]!;
  drawArticulatedLeg(c, cfg.far, '#3a1f10', 4.5);
  drawArticulatedLeg(c, cfg.near, P.BODY_DARK, 5.5);
}

function drawArticulatedLeg(
  c: CanvasRenderingContext2D,
  seg: LegSegments,
  upperColor: string,
  width: number,
): void {
  c.strokeStyle = upperColor;
  c.lineWidth = width;
  c.lineCap = 'round';
  c.beginPath();
  c.moveTo(seg.hipX, seg.hipY);
  c.lineTo(seg.kneeX, seg.kneeY);
  c.stroke();
  c.strokeStyle = P.BODY_DARK;
  c.lineWidth = Math.max(3, width - 1);
  c.beginPath();
  c.moveTo(seg.kneeX, seg.kneeY);
  c.lineTo(seg.footX, seg.footY);
  c.stroke();
  c.fillStyle = '#0d0703';
  c.beginPath();
  c.ellipse(seg.footX, seg.footY + 1, 4, 2.5, 0, 0, Math.PI * 2);
  c.fill();
}
